import React from "react";

import { Picto, TTableColumn } from "@amphore-dev/amphore-lib";
import { format } from "date-fns";
import { t } from "i18next";

import { Dot } from "@/components/atoms/Dot/Dot";

import { TProjectSummary } from "@/types";

const formatLastTask = (date: string | null) =>
	date ? format(new Date(date), "d MMM yyyy, HH:mm") : "—";

/**
 * Who shared a project: its workspace's name, else its owner's. Empty for
 * the user's own - most lists hold nothing else, so only the exceptions
 * say anything.
 */
const SHARED_BY_COLUMN = (): TTableColumn<TProjectSummary> => ({
	key: "workspace",
	label: t("projects.table.sharedBy"),
	width: "1fr",
	render: (project) =>
		!project.workspace || project.workspace.mine ? null : (
			<span data-testid="project-shared-by" className="font-medium">
				{project.workspace.name || project.workspace.ownerName}
			</span>
		),
});

/**
 * The /projects table (the "List" display). A function, not a constant:
 * `t()` is i18next's plain export, so the caller runs it after
 * i18next.init() (inside the component), or every label resolves to its
 * raw key.
 */
export const PROJECT_LIST_COLUMNS = (
	/** The list holds projects shared from other workspaces: say by whom. */
	withSharedBy = false
): TTableColumn<TProjectSummary>[] => [
	{
		key: "name",
		label: t("projects.table.name"),
		sortable: true,
		width: "2fr",
		render: (project) => (
			<span className="flex items-center gap-3 py-1">
				<Dot color={project.color} />
				<span className="flex min-w-0 flex-col">
					<span className="flex items-center gap-1.5 truncate font-medium">
						{project.name}
						{project.isArchived && (
							<span title={t("projects.infos.archived")}>
								<Picto
									icon="archive"
									className="h-3 w-3 text-neutral-400"
								/>
							</span>
						)}
					</span>
					{project.description && (
						<span className="truncate text-xs text-neutral-500">
							{project.description}
						</span>
					)}
				</span>
			</span>
		),
	},
	{
		key: "client",
		label: t("projects.table.client"),
		width: "1fr",
		render: (project) =>
			project.client ? (
				<span className="inline-flex items-center gap-1.5">
					<Dot
						color={project.client.color}
						className="h-2 w-2 min-w-2"
					/>
					{project.client.name}
				</span>
			) : (
				<span className="text-neutral-400">
					{t("projects.client.internalShort")}
				</span>
			),
	},
	...(withSharedBy ? [SHARED_BY_COLUMN()] : []),
	{
		key: "workedHours",
		label: t("projects.table.hours"),
		width: "auto",
		render: (project) => (
			<span className="font-medium">{project.workedHours.str}</span>
		),
	},
	{
		key: "tasksCount",
		label: t("projects.table.tasks"),
		sortable: true,
		width: "auto",
	},
	{
		key: "lastTaskDate",
		label: t("projects.table.lastTask"),
		sortable: true,
		width: "auto",
		render: (project) => formatLastTask(project.lastTaskDate),
	},
];
